/**
 * Footer Component
 * 
 * Zlúčený footer s dvoma variantami:
 * - full: Obsahuje newsletter sekciu + registráciu
 * - simple: Len registrácia (pre checkout, produktové stránky)
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { 
  Send, 
  ArrowUpRight, 
  Instagram, 
  Facebook, 
  Youtube, 
  Mail, 
  MapPin, 
  Phone, 
  UserPlus,
  Sparkles 
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { AuthDrawer } from "@/components/auth/AuthDrawer";
import { benefitIcons } from "@/lib/icons";
import mosaicLogo from "@/assets/mosaic-logo.svg";

interface FooterProps {
  variant?: 'full' | 'simple';
}

// Footer links configuration
const footerLinks = {
  shopping: [
    { label: "Darčekové poukážky", to: "/poukazka" },
    { label: "Interpreti", to: "/interpreti" },
  ],
  support: [
    { label: "Kontakt", to: "/kontakt" },
    { label: "Doprava a platba", to: "/doprava" },
    { label: "Sledovanie zásielky", to: "/tracking" },
  ],
  legal: [
    { label: "Ochrana súkromia", to: "/gdpr" },
  ],
};

// Dev links - všetky stránky pre testovanie
const devLinks = {
  core: [
    { label: "Domov", to: "/" },
    { label: "Kategória", to: "/kategoria/oblecenie" },
    { label: "Košík", to: "/kosik" },
    { label: "Pokladňa", to: "/pokladna" },
    { label: "Profil", to: "/profil" },
  ],
  products: [
    { label: "Produkt detail", to: "/produkt/1" },
    { label: "Jednoduchý produkt", to: "/produkt-simple/1" },
    { label: "Bundle", to: "/bundle/1" },
    { label: "Párový set", to: "/parovy-set/1" },
    { label: "Vlastný box", to: "/vlastny-box/1" },
    { label: "Balenie", to: "/balenie/1" },
    { label: "18+ produkt", to: "/18-plus/1" },
    { label: "Množstevná zľava", to: "/mnozstvo/1" },
    { label: "Bonus produkty", to: "/bonus-produkty" },
    { label: "Darčeky k nákupu", to: "/darceky" },
  ],
  partners: [
    { label: "Partner profil", to: "/partner/test" },
    { label: "Interpret", to: "/interpret/test" },
    { label: "Koza Bobkov", to: "/koza-bobkov" },
  ],
  internal: [
    { label: "Email podpis", to: "/email-podpis" },
    { label: "Email objednávka", to: "/email-objednavka" },
    { label: "Assety", to: "/assety" },
  ],
};

// Newsletter section component
const NewsletterSection = () => {
  const [email, setEmail] = useState("");
  const { toast } = useToast();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    
    toast({
      title: "Prihlásené!",
      description: "Ďakujeme za odber noviniek.",
    });
    setEmail("");
  };

  return (
    <div className="relative lg:pr-12 py-6 lg:border-r border-footer-foreground/10">
      <div className="absolute inset-0 bg-footer-foreground/[0.02] rounded-2xl lg:rounded-r-none -z-10" />
      
      <p className="text-primary text-sm font-bold uppercase tracking-widest mb-2">Newsletter</p>
      <h3 className="text-2xl md:text-3xl font-bold mb-2">Získaj 10% zľavu</h3>
      <p className="text-footer-foreground/60 text-sm mb-5">
        Prihláste sa a dostávajte exkluzívne ponuky priamo do schránky.
      </p>
      <form onSubmit={handleSubmit} className="flex gap-2 w-full">
        <div className="relative flex-1">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-footer-foreground/40" />
          <Input
            type="email"
            placeholder="vas@email.sk"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="h-12 pl-10 bg-footer-foreground/10 border-footer-foreground/20 text-footer-foreground placeholder:text-footer-foreground/40 rounded-xl focus:border-primary focus:ring-primary"
          />
        </div>
        <Button 
          type="submit" 
          size="lg" 
          className="h-12 px-6 gap-2 bg-primary text-primary-foreground hover:bg-primary/80 hover:scale-105 hover:shadow-lg hover:shadow-primary/30 rounded-xl transition-all"
        >
          <Send className="w-4 h-4" />
          <span className="hidden sm:inline">Prihlásiť</span>
        </Button>
      </form>
    </div>
  );
};

// Registration section component
const RegistrationSection = ({ 
  onOpenAuth, 
  isFullWidth = false 
}: { 
  onOpenAuth: () => void;
  isFullWidth?: boolean;
}) => {
  return (
    <div className={`relative py-6 ${!isFullWidth ? 'lg:pl-12' : ''}`}>
      <div className="absolute inset-0 bg-primary/[0.03] rounded-2xl lg:rounded-l-none -z-10" />
      
      <div className={`flex flex-col lg:flex-row lg:items-end lg:justify-between h-full gap-5 ${isFullWidth ? 'items-center' : ''}`}>
        <div className={isFullWidth ? 'text-center lg:text-left flex-1' : ''}>
          <div className={`inline-flex items-center gap-2 mb-3 ${isFullWidth ? '' : ''}`}>
            {isFullWidth && (
              <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-primary" />
              </div>
            )}
            <p className="text-primary text-sm font-bold uppercase tracking-widest">Nový zákazník?</p>
          </div>
          <h3 className="text-2xl md:text-3xl font-bold mb-4">
            {isFullWidth ? 'Vytvor si účet zadarmo' : 'Vytvor si účet'}
          </h3>
          <ul className={`flex flex-wrap gap-x-6 gap-y-1.5 ${isFullWidth ? 'justify-center lg:justify-start gap-y-2' : ''}`}>
            <li className="flex items-center gap-2 text-sm text-footer-foreground/70">
              <benefitIcons.rychlejsiNakup className="w-4 h-4 text-primary flex-shrink-0" />
              Rýchlejší nákup
            </li>
            <li className="flex items-center gap-2 text-sm text-footer-foreground/70">
              <benefitIcons.viacAdries className="w-4 h-4 text-primary flex-shrink-0" />
              Viac adries doručenia
            </li>
            <li className="flex items-center gap-2 text-sm text-footer-foreground/70">
              <benefitIcons.historiaObjednavok className="w-4 h-4 text-primary flex-shrink-0" />
              História objednávok
            </li>
          </ul>
        </div>
        
        <Button 
          onClick={onOpenAuth}
          size="lg" 
          className="h-12 px-6 gap-2 bg-primary text-primary-foreground hover:bg-primary/80 hover:scale-105 hover:shadow-lg hover:shadow-primary/30 rounded-xl transition-all shrink-0"
        >
          <UserPlus className="w-4 h-4" />
          <span className="font-semibold">Registrovať sa</span>
        </Button>
      </div>
    </div>
  );
};

// Footer link component
const FooterLink = ({ to, children }: { to: string; children: React.ReactNode }) => (
  <Link 
    to={to} 
    className="text-sm text-footer-foreground/60 hover:text-primary transition-colors flex items-center gap-1 group"
  >
    {children}
    <ArrowUpRight className="w-3 h-3 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
  </Link>
);

export const Footer = ({ variant = 'full' }: FooterProps) => {
  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const isSimple = variant === 'simple';

  return (
    <footer className="bg-footer text-footer-foreground relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-primary/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 right-0 w-80 h-80 bg-primary/5 rounded-full blur-3xl translate-x-1/3 translate-y-1/3" />
      
      {/* Top Section - Newsletter + Registration OR just Registration */}
      <div className="relative border-b border-footer-foreground/10">
        <div className="max-w-[1600px] mx-auto px-4 lg:px-6 py-8">
          {isSimple ? (
            <div className="flex flex-col md:flex-row items-center justify-between gap-8">
              <RegistrationSection onOpenAuth={() => setIsAuthOpen(true)} isFullWidth />
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
              <NewsletterSection />
              <div className="lg:hidden w-full h-px bg-gradient-to-r from-transparent via-footer-foreground/20 to-transparent" />
              <RegistrationSection onOpenAuth={() => setIsAuthOpen(true)} />
            </div>
          )}
        </div>
      </div>

      <AuthDrawer isOpen={isAuthOpen} onClose={() => setIsAuthOpen(false)} />

      {/* Main Footer Content */}
      <div className="relative max-w-[1600px] mx-auto px-4 lg:px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          {/* Brand + Contact */}
          <div className="md:col-span-4">
            <img src={mosaicLogo} alt="Mosaic" className="h-10 mb-6 brightness-0 invert opacity-90" loading="lazy" />
            <p className="text-footer-foreground/60 text-sm mb-6 leading-relaxed">
              Prémiový merch od vašich obľúbených umelcov. Kvalita a originálny dizajn na prvom mieste.
            </p>
            
            {/* Contact info */}
            <div className="space-y-3">
              <a href="mailto:info@mosaic.sk" className="flex items-center gap-3 text-sm text-footer-foreground/60 hover:text-footer-foreground transition-colors group">
                <Mail className="w-4 h-4 text-primary" />
                info@mosaic.sk
              </a>
              <a href="tel:+421900123456" className="flex items-center gap-3 text-sm text-footer-foreground/60 hover:text-footer-foreground transition-colors group">
                <Phone className="w-4 h-4 text-primary" />
                +421 900 123 456
              </a>
              <div className="flex items-center gap-3 text-sm text-footer-foreground/60">
                <MapPin className="w-4 h-4 text-primary" />
                Bratislava, Slovensko
              </div>
            </div>

            {/* Social links */}
            <div className="flex items-center gap-2 mt-6">
              {[
                { icon: Instagram, label: 'Instagram' },
                { icon: Facebook, label: 'Facebook' },
                { icon: Youtube, label: 'Youtube' },
              ].map(({ icon: Icon, label }) => (
                <a 
                  key={label}
                  href="#" 
                  aria-label={label}
                  className="w-10 h-10 rounded-xl bg-footer-foreground/5 border border-footer-foreground/10 flex items-center justify-center hover:bg-primary hover:border-primary transition-all group"
                >
                  <Icon className="w-4 h-4 text-footer-foreground/70 group-hover:text-primary-foreground transition-colors" />
                </a>
              ))}
            </div>
          </div>

          {/* Links */}
          <div className="md:col-span-8">
            <div className={`grid grid-cols-2 ${isSimple ? 'sm:grid-cols-3' : 'sm:grid-cols-3'} gap-8`}>
              <div>
                <h4 className="font-bold mb-5 text-footer-foreground text-sm uppercase tracking-wider">Nakupovanie</h4>
                <ul className="space-y-3">
                  {footerLinks.shopping.map(link => (
                    <li key={link.to}>
                      <FooterLink to={link.to}>{link.label}</FooterLink>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="font-bold mb-5 text-footer-foreground text-sm uppercase tracking-wider">Podpora</h4>
                <ul className="space-y-3">
                  {footerLinks.support.map(link => (
                    <li key={link.to}>
                      <FooterLink to={link.to}>{link.label}</FooterLink>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="font-bold mb-5 text-footer-foreground text-sm uppercase tracking-wider">Právne</h4>
                <ul className="space-y-3">
                  {footerLinks.legal.map(link => (
                    <li key={link.to}>
                      <FooterLink to={link.to}>{link.label}</FooterLink>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Dev Links Section */}
            <div className="mt-10 pt-8 border-t border-footer-foreground/10">
              <h4 className="font-bold mb-5 text-footer-foreground/50 text-xs uppercase tracking-wider">🛠️ Dev / Všetky stránky</h4>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
                <div>
                  <p className="text-xs text-footer-foreground/40 mb-2 uppercase">Core</p>
                  <ul className="space-y-1.5">
                    {devLinks.core.map(link => (
                      <li key={link.to}>
                        <FooterLink to={link.to}>{link.label}</FooterLink>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="text-xs text-footer-foreground/40 mb-2 uppercase">Produkty</p>
                  <ul className="space-y-1.5">
                    {devLinks.products.map(link => (
                      <li key={link.to}>
                        <FooterLink to={link.to}>{link.label}</FooterLink>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="text-xs text-footer-foreground/40 mb-2 uppercase">Partneri</p>
                  <ul className="space-y-1.5">
                    {devLinks.partners.map(link => (
                      <li key={link.to}>
                        <FooterLink to={link.to}>{link.label}</FooterLink>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="text-xs text-footer-foreground/40 mb-2 uppercase">Interné</p>
                  <ul className="space-y-1.5">
                    {devLinks.internal.map(link => (
                      <li key={link.to}>
                        <FooterLink to={link.to}>{link.label}</FooterLink>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="relative border-t border-footer-foreground/10">
        <div className="max-w-[1600px] mx-auto px-4 lg:px-6 py-6">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-xs text-footer-foreground/40">© 2025 Merch s.r.o. Všetky práva vyhradené.</p>
            
            {/* Payment methods */}
            <div className="flex items-center gap-4">
              <span className="text-xs text-footer-foreground/40 hidden sm:inline">Platobné metódy:</span>
              <div className="flex items-center gap-3">
                <img src="https://upload.wikimedia.org/wikipedia/commons/5/5e/Visa_Inc._logo.svg" alt="Visa" className="h-4 opacity-50 brightness-0 invert hover:opacity-80 transition-opacity" loading="lazy" />
                <img src="https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg" alt="Mastercard" className="h-4 opacity-50 hover:opacity-80 transition-opacity" loading="lazy" />
                <img src="https://upload.wikimedia.org/wikipedia/commons/b/b0/Apple_Pay_logo.svg" alt="Apple Pay" className="h-4 opacity-50 brightness-0 invert hover:opacity-80 transition-opacity" loading="lazy" />
                <img src="https://upload.wikimedia.org/wikipedia/commons/f/f2/Google_Pay_Logo.svg" alt="Google Pay" className="h-4 opacity-50 hover:opacity-80 transition-opacity" loading="lazy" />
              </div>
            </div>

            <a
              href="https://mosaic.sk"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-xs text-footer-foreground/40 hover:text-primary transition-colors group"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
              Powered by MOSAIC
              <ArrowUpRight className="w-3 h-3 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
